import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiCheck,
  FiMail,
  FiLock,
  FiArrowRight,
  FiBook,
  FiAward,
  FiEye,
  FiEyeOff,
  FiInfo,
  FiX,
  FiRefreshCw,
  FiUser,
  FiMapPin,
  FiCompass
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import boardService, { BoardMaster } from '../services/boardService';

// Form Data Interface for Parent-Focused Registration
export interface RegisterFormDataNew {
  parentEmail: string;
  parentPassword: string;
  parentVerified: boolean;
  studentFirstName: string;
  studentLastName: string;
  state: string;
  schoolName: string;
  parentWhatsappNumber: string;
  board: string;
  standard: string;
  medium: string;
  selectedSubjects: string[];
}

const initialFormData: RegisterFormDataNew = {
  parentEmail: '',
  parentPassword: '',
  parentVerified: false,
  studentFirstName: '',
  studentLastName: '',
  state: '',
  schoolName: '',
  parentWhatsappNumber: '',
  board: '',
  standard: '',
  medium: '',
  selectedSubjects: [],
};

const RegisterPageNew: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Step state (1: Email, 2: OTP Verification, 3: Password Setup, 4: Parent Details, 5: Academic Details, 6: Subject Selection & Submit, 7: Welcome)
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<RegisterFormDataNew>(initialFormData);

  // Resume registration progress from login or state state
  useEffect(() => {
    const state = location.state as { resumeEmail?: string; step?: number };
    if (state?.resumeEmail) {
      setFormData(prev => ({ ...prev, parentEmail: state.resumeEmail || '' }));
      if (state.step) {
        setStep(state.step);
        toast.success('Resuming your incomplete registration!');
      }
    }
  }, [location.state]);

  // Verification & Resend States
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);

  // Resend Timer (20s interval, max 3 resends)
  const [resendTimer, setResendTimer] = useState(0);
  const [resendCount, setResendCount] = useState(0);

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validation errors state
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [standardsList, setStandardsList] = useState<any[]>([]);
  const [subjectsList, setSubjectsList] = useState<any[]>([]);
  const [mediumsList, setMediumsList] = useState<any[]>([]);
  const [fetchingOptions, setFetchingOptions] = useState(false);
  const [activeBoards, setActiveBoards] = useState<BoardMaster[]>([]);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [boardsData, mediumsRes] = await Promise.all([
          boardService.getActiveBoards(),
          fetch('/api/student/register/mediums')
        ]);
        setActiveBoards(boardsData);
        if (mediumsRes.ok) {
          const mediumsData = await mediumsRes.json();
          if (Array.isArray(mediumsData)) {
            setMediumsList(mediumsData);
          }
        }
      } catch (err) {
        console.error('Failed to fetch master data', err);
      }
    };
    fetchMasterData();
  }, []);

  // Resend Timer Effect
  useEffect(() => {
    if (resendTimer <= 0) return;
    const timerId = setInterval(() => {
      setResendTimer(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [resendTimer]);

  // Sync state to DB on step completion
  const persistStateToDB = async (nextStep: number, updatedData: RegisterFormDataNew) => {
    try {
      const sanitizedData = {
        ...updatedData,
        parentMobile: updatedData.parentWhatsappNumber,
        studentEmail: updatedData.parentEmail
      };
      delete (sanitizedData as any).parentPassword;

      await fetch('/api/student/register-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: updatedData.parentEmail,
          currentStep: nextStep,
          formData: sanitizedData
        })
      });
    } catch (err) {
      console.warn('Unable to sync registration state to database:', err);
    }
  };

  const handleNextStep = (next: number, dataOverride?: Partial<RegisterFormDataNew>) => {
    const updated = { ...formData, ...dataOverride };
    setFormData(updated);
    setStep(next);
    persistStateToDB(next, updated);
  };

  const updateField = (field: keyof RegisterFormDataNew, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Password Policy Checks
  const validatePassword = (pass: string) => {
    if (pass.length < 8) return false;
    const hasUpper = /[A-Z]/.test(pass);
    const hasLower = /[a-z]/.test(pass);
    const hasDigit = /[0-9]/.test(pass);
    const hasSpecial = /[!@#$%^&*(),.?"{}|<>_+=\-[\]\\\/]/.test(pass);
    return hasUpper && hasLower && hasDigit && hasSpecial;
  };

  const meetsMinLength = formData.parentPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(formData.parentPassword);
  const hasLowercase = /[a-z]/.test(formData.parentPassword);
  const hasDigit = /[0-9]/.test(formData.parentPassword);
  const hasSpecial = /[!@#$%^&*(),.?"{}|<>_+=\-[\]\\\/]/.test(formData.parentPassword);

  // Fetch standards when board changes
  useEffect(() => {
    if (!formData.board) return;

    const fetchStandards = async () => {
      try {
        setFetchingOptions(true);
        const response = await fetch(`/api/student/register/standards?board=${encodeURIComponent(formData.board)}`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setStandardsList(data);
            if (formData.standard && !data.some((s: any) => s.name === formData.standard)) {
              updateField('standard', '');
            }
            return;
          }
        }
      } catch (err) {
        console.warn('Backend API standards fetch warning (using defaults):', err);
      } finally {
        setFetchingOptions(false);
      }

      // Default standards fallback ensures dropdown never breaks
      const fallbackData = [
        { id: 1, name: 'Grade 7', board: formData.board },
        { id: 2, name: 'Grade 8', board: formData.board },
        { id: 3, name: 'Grade 9', board: formData.board }
      ];
      setStandardsList(fallbackData);
    };

    fetchStandards();
  }, [formData.board]);

  // Fetch subjects when standard or board changes
  useEffect(() => {
    if (!formData.board || !formData.standard) return;

    const selectedStandardObj = standardsList.find(s => s.name === formData.standard);
    const standardId = selectedStandardObj ? selectedStandardObj.id : 1;

    const fetchSubjects = async () => {
      try {
        setFetchingOptions(true);
        const response = await fetch(`/api/student/register/subjects?board=${encodeURIComponent(formData.board)}&standardId=${standardId}`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setSubjectsList(data);
            const subjectCodes = data.map((sub: any) => sub.code || sub.name);
            updateField('selectedSubjects', subjectCodes);
            return;
          }
        }
      } catch (err) {
        console.warn('Backend API subjects fetch warning (using defaults):', err);
      } finally {
        setFetchingOptions(false);
      }

      // Default subjects fallback ensures trial subject step never breaks
      const fallbackSubjects = [
        { id: 1, name: 'Mathematics', code: 'MATH' },
        { id: 2, name: 'Science', code: 'SCI' },
        { id: 3, name: 'English', code: 'ENG' },
        { id: 4, name: 'Social Studies', code: 'SST' }
      ];
      setSubjectsList(fallbackSubjects);
      updateField('selectedSubjects', fallbackSubjects.map(s => s.code));
    };

    fetchSubjects();
  }, [formData.board, formData.standard, standardsList]);

  // SCREEN 1: Check Email & Initiate Verification
  const handleCheckParentEmail = async () => {
    setUserAlreadyExists(false);
    const newErrors: Record<string, string> = {};
    if (!formData.parentEmail) {
      newErrors.parentEmail = 'Parent Email address is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.parentEmail)) {
        newErrors.parentEmail = 'Please enter a valid email address.';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setLoading(true);

      // Check registration status in DB first
      const statusRes = await fetch(`/api/student/register-status?email=${encodeURIComponent(formData.parentEmail)}`);
      if (statusRes.ok) {
        const savedState = await statusRes.json();
        if (savedState && (savedState.currentStep === 7 || savedState.isCompleted)) {
          setUserAlreadyExists(true);
          toast.error('An account with this email is already fully registered.');
          return;
        }
        if (savedState && savedState.formData && savedState.currentStep > 1) {
          const sfName = savedState.formData.studentFirstName || savedState.formData.parentFirstName || '';
          const slName = savedState.formData.studentLastName || savedState.formData.parentLastName || '';
          const phone = savedState.formData.parentWhatsappNumber || savedState.formData.parentMobile || '';
          const email = savedState.formData.parentEmail || savedState.formData.studentEmail || formData.parentEmail;
          setFormData(prev => ({
            ...prev,
            ...savedState.formData,
            parentEmail: email,
            studentFirstName: sfName,
            studentLastName: slName,
            parentWhatsappNumber: phone,
            selectedSubjects: savedState.formData.selectedSubjects || []
          }));
          setStep(savedState.currentStep);
          toast.success('Found your incomplete registration! Resuming where you left off.');
          return;
        }
      }

      // Initiate verification in AWS Cognito / DB
      const initRes = await fetch('/api/student/register/initiate-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.parentEmail, role: 'Parent' })
      });

      if (!initRes.ok) {
        const errJson = await initRes.json();
        if (errJson.message && errJson.message.toLowerCase().includes('already registered')) {
          setUserAlreadyExists(true);
          toast.error('An account with this email already exists.');
          return;
        }
        throw new Error(errJson.message || 'Failed to send verification email.');
      }

      toast.success('Verification code sent to your email!');
      setResendTimer(20);
      setResendCount(0);
      handleNextStep(2);
    } catch (err: any) {
      toast.error(err.message || 'Error initiating verification.');
    } finally {
      setLoading(false);
    }
  };

  // SCREEN 2: OTP Verification & Resend Handler
  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setErrors({ otpCode: 'Please enter the 6-digit verification code.' });
      return;
    }

    setErrors({});

    try {
      setLoading(true);
      const verifyRes = await fetch('/api/student/register/confirm-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.parentEmail,
          code: otpCode
        })
      });

      if (!verifyRes.ok) {
        const errJson = await verifyRes.json();
        throw new Error(errJson.message || 'Invalid verification code.');
      }

      toast.success('Email verified successfully!');
      handleNextStep(3, { parentVerified: true });
    } catch (err: any) {
      toast.error(err.message || 'OTP verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0 || resendCount >= 3) return;

    try {
      setLoading(true);
      const initRes = await fetch('/api/student/register/initiate-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.parentEmail, role: 'Parent' })
      });

      if (!initRes.ok) {
        const errJson = await initRes.json();
        throw new Error(errJson.message || 'Failed to resend verification email.');
      }

      toast.success('New OTP sent to your email!');
      setResendTimer(20);
      setResendCount(prev => prev + 1);
    } catch (err: any) {
      toast.error(err.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  // SCREEN 3: Password Setup
  const handleSavePassword = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.parentPassword) {
      newErrors.parentPassword = 'Password is required.';
    } else if (!validatePassword(formData.parentPassword)) {
      newErrors.parentPassword = 'Password must meet all complexity rules.';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password.';
    } else if (confirmPassword !== formData.parentPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setLoading(true);
      const verifyRes = await fetch('/api/student/register/confirm-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.parentEmail,
          code: otpCode,
          password: formData.parentPassword
        })
      });

      if (!verifyRes.ok) {
        const errJson = await verifyRes.json();
        throw new Error(errJson.message || 'Failed to save password.');
      }

      handleNextStep(4);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save password.');
    } finally {
      setLoading(false);
    }
  };

  // SCREEN 4: Student Details
  const handleSaveParentDetails = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.studentFirstName.trim()) {
      newErrors.studentFirstName = "Student's first name is required.";
    }
    if (!formData.studentLastName.trim()) {
      newErrors.studentLastName = "Student's last name is required.";
    }
    if (!formData.state.trim()) {
      newErrors.state = 'Please select or enter a state.';
    }
    if (!formData.schoolName.trim()) {
      newErrors.schoolName = 'School name is required.';
    }
    if (!formData.parentWhatsappNumber.trim()) {
      newErrors.parentWhatsappNumber = 'WhatsApp number is required.';
    } else {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(formData.parentWhatsappNumber.replace(/\D/g, ''))) {
        newErrors.parentWhatsappNumber = 'Please enter a valid 10-digit WhatsApp number.';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all required fields marked with *');
      return;
    }

    setErrors({});
    handleNextStep(5);
  };

  // SCREEN 5: Academic Details
  const handleSaveAcademicDetails = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.board) {
      newErrors.board = 'Please select a board.';
    }
    if (!formData.medium) {
      newErrors.medium = 'Please select a medium.';
    }
    if (!formData.standard) {
      newErrors.standard = 'Please select a standard/grade.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please complete all academic options to proceed.');
      return;
    }

    setErrors({});
    handleNextStep(6);
  };

  // SCREEN 6: Complete Registration & Final Onboarding
  const handleCompleteOnboarding = async () => {
    try {
      setLoading(true);

      const requestPayload = {
        student: {
          firstName: formData.studentFirstName,
          lastName: formData.studentLastName,
          email: formData.parentEmail,
          password: formData.parentPassword,
          state: formData.state,
          schoolName: formData.schoolName,
          phoneNumber: formData.parentWhatsappNumber,
          address: ''
        },
        parent: {
          firstName: 'Parent of',
          lastName: formData.studentFirstName || 'Student',
          phoneNumber: formData.parentWhatsappNumber,
          email: formData.parentEmail,
          password: formData.parentPassword,
          sameAsStudentEmail: true
        },
        academic: {
          board: formData.board,
          standard: formData.standard,
          medium: formData.medium
        },
        subjects: formData.selectedSubjects
      };

      const response = await fetch('/api/users/registration/student-onboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMsg = 'Onboarding registration failed.';
        if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMsg = errorData.errors.join(' ');
        } else if (errorData.message) {
          errorMsg = errorData.message;
        }
        throw new Error(errorMsg);
      }

      // Mark completion in DB
      await fetch(`/api/student/register-status/complete?email=${encodeURIComponent(formData.parentEmail)}`, {
        method: 'POST'
      });

      toast.success('Registration complete!');
      handleNextStep(7);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  // SCREEN 7: Confetti effect on step 7
  useEffect(() => {
    if (step !== 7) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Array<{
      x: number;
      y: number;
      size: number;
      color: string;
      speedX: number;
      speedY: number;
      rotation: number;
      rotSpeed: number;
    }> = [];

    const colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#6366F1'];

    for (let i = 0; i < 120; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        size: Math.random() * 8 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        speedX: (Math.random() - 0.5) * 4,
        speedY: Math.random() * 3 + 2,
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 5,
      });
    }

    let animId: number;
    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.rotation += p.rotSpeed;

        if (p.y > canvas.height) {
          p.y = -10;
          p.x = Math.random() * canvas.width;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        ctx.restore();
      });

      animId = requestAnimationFrame(render);
    };

    render();
    return () => cancelAnimationFrame(animId);
  }, [step]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/40 to-slate-50 flex items-center justify-center p-4 sm:p-6 md:p-10 relative overflow-hidden">
      {/* Confetti canvas for step 7 */}
      {step === 7 && (
        <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-50" />
      )}

      {/* Background Orbs */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-300/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-purple-300/20 blur-3xl pointer-events-none" />

      <div className="w-full max-w-xl bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 p-6 sm:p-10 relative z-10">

        {/* Header & Step Indicator */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100/60 text-blue-700 font-semibold text-xs mb-3 tracking-wide uppercase">
            <FiAward className="w-4 h-4" /> Parent Onboarding Portal
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            {step === 1 && 'Get Started with Masti Manch'}
            {step === 2 && 'Verify Parent Email'}
            {step === 3 && 'Create Your Password'}
            {step === 4 && 'Student & Profile Details'}
            {step === 5 && 'Academic Details'}
            {step === 6 && 'Trial Subject Plan'}
            {step === 7 && 'Welcome to Masti Manch!'}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {step === 1 && "Enter parent email to start your student's 7-day trial"}
            {step === 2 && `We've sent a 6-digit code to ${formData.parentEmail}`}
            {step === 3 && 'Secure your parent management account'}
            {step === 4 && "Provide your student's details, state, school & parent contact"}
            {step === 5 && "Select your child's board, medium, and grade"}
            {step === 6 && 'Review your included trial curriculum'}
            {step === 7 && 'Your account is ready to launch!'}
          </p>

          {/* Stepper Dots (1 to 6) */}
          {step <= 6 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div
                  key={i}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === step
                      ? 'w-8 bg-blue-600'
                      : i < step
                      ? 'w-2 bg-blue-400'
                      : 'w-2 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* User Already Exists Modal/Notice Banner */}
        {userAlreadyExists && (
          <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <FiInfo className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">Account Already Exists</p>
                <p className="text-xs text-amber-800 mt-0.5">
                  An account with <strong>{formData.parentEmail}</strong> is already registered in our system.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-1">
              <button
                onClick={() => navigate('/login')}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-xs transition"
              >
                Log In
              </button>
              <button
                onClick={() => navigate('/forgot-password')}
                className="flex-1 py-2 bg-white hover:bg-amber-100 text-amber-900 border border-amber-300 rounded-xl font-semibold text-xs transition"
              >
                Forgot Password?
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 1: Parent Email */}
        {step === 1 && !userAlreadyExists && (
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                Parent Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={formData.parentEmail}
                  onChange={e => updateField('parentEmail', e.target.value)}
                  placeholder="parent@example.com"
                  className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border ${
                    errors.parentEmail ? 'border-red-500 bg-red-50/30' : 'border-gray-200'
                  } rounded-2xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition`}
                />
              </div>
              {errors.parentEmail && (
                <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.parentEmail}</p>
              )}
            </div>

            <button
              onClick={handleCheckParentEmail}
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-blue-500/25 transition duration-200 flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Continue & Send Code <FiArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <div className="text-center text-xs text-gray-500 pt-2">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 font-bold hover:underline">
                Log In
              </Link>
            </div>
          </div>
        )}

        {/* SCREEN 2: OTP Verification */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                6-Digit Verification Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                maxLength={6}
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className={`w-full tracking-widest text-center text-2xl font-bold py-4 bg-gray-50 border ${
                  errors.otpCode ? 'border-red-500' : 'border-gray-200'
                } rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition`}
              />
              {errors.otpCode && (
                <p className="text-red-500 text-xs mt-1.5 font-medium text-center">{errors.otpCode}</p>
              )}
            </div>

            <div className="flex items-center justify-between text-xs bg-gray-50 p-3.5 rounded-xl border border-gray-100">
              <span className="text-gray-600 font-medium">Didn't receive the code?</span>
              <button
                onClick={handleResendOTP}
                disabled={resendTimer > 0 || resendCount >= 3 || loading}
                className="font-bold text-blue-600 hover:text-blue-700 disabled:text-gray-400 flex items-center gap-1.5"
              >
                <FiRefreshCw className={`w-3.5 h-3.5 ${resendTimer > 0 ? 'animate-spin' : ''}`} />
                {resendTimer > 0
                  ? `Resend in ${resendTimer}s`
                  : resendCount >= 3
                  ? 'Max resends reached'
                  : 'Resend Code'}
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="w-1/3 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-2xl transition text-sm"
              >
                Back
              </button>
              <button
                onClick={handleVerifyOTP}
                disabled={loading}
                className="w-2/3 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-blue-500/25 transition flex items-center justify-center gap-2 text-sm disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Verify & Continue <FiArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 3: Password Setup */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.parentPassword}
                  onChange={e => updateField('parentPassword', e.target.value)}
                  placeholder="••••••••"
                  className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 border ${
                    errors.parentPassword ? 'border-red-500' : 'border-gray-200'
                  } rounded-2xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                </button>
              </div>
              {errors.parentPassword && (
                <p className="text-red-500 text-xs mt-1 font-medium">{errors.parentPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 border ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-200'
                  } rounded-2xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1 font-medium">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Password Policy Checklist */}
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-2 text-xs">
              <p className="font-bold text-gray-700 mb-1">Password Requirements:</p>
              <div className="grid grid-cols-2 gap-2">
                <div className={`flex items-center gap-1.5 ${meetsMinLength ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                  <FiCheck className={`w-4 h-4 ${meetsMinLength ? 'text-green-600' : 'text-gray-300'}`} />
                  At least 8 characters
                </div>
                <div className={`flex items-center gap-1.5 ${hasUppercase ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                  <FiCheck className={`w-4 h-4 ${hasUppercase ? 'text-green-600' : 'text-gray-300'}`} />
                  Uppercase letter (A-Z)
                </div>
                <div className={`flex items-center gap-1.5 ${hasLowercase ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                  <FiCheck className={`w-4 h-4 ${hasLowercase ? 'text-green-600' : 'text-gray-300'}`} />
                  Lowercase letter (a-z)
                </div>
                <div className={`flex items-center gap-1.5 ${hasDigit ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                  <FiCheck className={`w-4 h-4 ${hasDigit ? 'text-green-600' : 'text-gray-300'}`} />
                  Number (0-9)
                </div>
                <div className={`flex items-center gap-1.5 col-span-2 ${hasSpecial ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                  <FiCheck className={`w-4 h-4 ${hasSpecial ? 'text-green-600' : 'text-gray-300'}`} />
                  Special character (!@#$%^&*)
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep(2)}
                className="w-1/3 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-2xl transition text-sm"
              >
                Back
              </button>
              <button
                onClick={handleSavePassword}
                className="w-2/3 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-blue-500/25 transition flex items-center justify-center gap-2 text-sm"
              >
                Next Details <FiArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 4: Student & Location Details */}
        {step === 4 && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                  Student's First Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.studentFirstName}
                    onChange={e => updateField('studentFirstName', e.target.value)}
                    placeholder="Student's First Name"
                    className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border ${
                      errors.studentFirstName ? 'border-red-500' : 'border-gray-200'
                    } rounded-2xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition`}
                  />
                </div>
                {errors.studentFirstName && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{errors.studentFirstName}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                  Student's Last Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.studentLastName}
                    onChange={e => updateField('studentLastName', e.target.value)}
                    placeholder="Student's Last Name"
                    className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border ${
                      errors.studentLastName ? 'border-red-500' : 'border-gray-200'
                    } rounded-2xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition`}
                  />
                </div>
                {errors.studentLastName && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{errors.studentLastName}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    value={formData.state}
                    onChange={e => updateField('state', e.target.value)}
                    className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border ${
                      errors.state ? 'border-red-500' : 'border-gray-200'
                    } rounded-2xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition appearance-none`}
                  >
                    <option value="">Select State</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {errors.state && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{errors.state}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                  School Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiCompass className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.schoolName}
                    onChange={e => updateField('schoolName', e.target.value)}
                    placeholder="Enter School Name"
                    className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border ${
                      errors.schoolName ? 'border-red-500' : 'border-gray-200'
                    } rounded-2xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition`}
                  />
                </div>
                {errors.schoolName && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{errors.schoolName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                Parent WhatsApp Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FaWhatsapp className="absolute left-4 top-1/2 -translate-y-1/2 text-green-500 w-5 h-5" />
                <input
                  type="tel"
                  maxLength={10}
                  value={formData.parentWhatsappNumber}
                  onChange={e => updateField('parentWhatsappNumber', e.target.value.replace(/\D/g, ''))}
                  placeholder="10-digit WhatsApp Number"
                  className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border ${
                    errors.parentWhatsappNumber ? 'border-red-500' : 'border-gray-200'
                  } rounded-2xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition`}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Used for progress reports & class reminders.</p>
              {errors.parentWhatsappNumber && (
                <p className="text-red-500 text-xs mt-1 font-medium">{errors.parentWhatsappNumber}</p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep(3)}
                className="w-1/3 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-2xl transition text-sm"
              >
                Back
              </button>
              <button
                onClick={handleSaveParentDetails}
                className="w-2/3 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-blue-500/25 transition flex items-center justify-center gap-2 text-sm"
              >
                Academic Setup <FiArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 5: Academic Details */}
        {step === 5 && (
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                Education Board <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {activeBoards.map(b => {
                  const boardVal = b.code === 'STATE' ? 'State Board' : b.code;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => updateField('board', boardVal)}
                      className={`py-3.5 px-4 rounded-2xl font-bold text-sm border transition flex items-center justify-center gap-2 ${
                        formData.board === boardVal
                          ? 'bg-blue-50 border-blue-600 text-blue-700 shadow-sm'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <FiBook className="w-4 h-4" /> {b.name} ({b.code})
                    </button>
                  );
                })}
              </div>
              {errors.board && <p className="text-red-500 text-xs mt-1 font-medium">{errors.board}</p>}
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                Medium of Instruction <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(mediumsList.length > 0 ? mediumsList : [{ id: 1, mediumName: 'English' }, { id: 2, mediumName: 'Hindi' }, { id: 3, mediumName: 'Gujarati' }]).map(m => {
                  const name = m.mediumName || m.name || m;
                  return (
                    <button
                      key={m.id || name}
                      type="button"
                      onClick={() => updateField('medium', name)}
                      className={`py-3 px-3 rounded-2xl font-bold text-xs border transition flex items-center justify-center ${
                        formData.medium === name
                          ? 'bg-blue-50 border-blue-600 text-blue-700 shadow-sm'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
              {errors.medium && <p className="text-red-500 text-xs mt-1 font-medium">{errors.medium}</p>}
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                Grade / Standard <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.standard}
                onChange={e => updateField('standard', e.target.value)}
                disabled={!formData.board || fetchingOptions}
                className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition disabled:opacity-50"
              >
                <option value="">
                  {!formData.board
                    ? 'Select Education Board First'
                    : fetchingOptions
                    ? 'Loading Standards...'
                    : 'Choose Standard'}
                </option>
                {standardsList.map(s => (
                  <option key={s.id || s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
              {errors.standard && <p className="text-red-500 text-xs mt-1 font-medium">{errors.standard}</p>}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep(4)}
                className="w-1/3 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-2xl transition text-sm"
              >
                Back
              </button>
              <button
                onClick={handleSaveAcademicDetails}
                className="w-2/3 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-blue-500/25 transition flex items-center justify-center gap-2 text-sm"
              >
                Review Curriculum <FiArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 6: Trial Subject Selection & Final Onboarding */}
        {step === 6 && (
          <div className="space-y-6">
            {/* Trial Info Banner */}
            <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/20 flex items-start gap-3">
              <FiAward className="w-6 h-6 flex-shrink-0 mt-0.5 text-yellow-300" />
              <div>
                <h3 className="font-bold text-sm">7-Day Full Access Free Trial</h3>
                <p className="text-xs text-blue-100 mt-1 leading-relaxed">
                  All subjects are included automatically for your 7-day free trial. No credit card required!
                </p>
              </div>
            </div>

            {/* Included Subjects List */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-3">
                Included Subjects ({subjectsList.length})
              </label>

              {fetchingOptions ? (
                <div className="p-6 text-center text-gray-400 text-sm">Loading subjects...</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {subjectsList.map((sub, idx) => (
                    <div
                      key={sub.code || sub.name || idx}
                      className="p-3.5 bg-gray-50 rounded-2xl border border-gray-200 flex items-center justify-between"
                    >
                      <span className="font-semibold text-sm text-gray-800">{sub.name || sub.code}</span>
                      <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold shadow-sm">
                        <FiCheck className="w-4 h-4" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep(5)}
                className="w-1/3 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-2xl transition text-sm"
              >
                Back
              </button>
              <button
                onClick={handleCompleteOnboarding}
                disabled={loading}
                className="w-2/3 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold rounded-2xl shadow-lg shadow-green-500/25 transition flex items-center justify-center gap-2 text-sm disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Complete Sign Up <FiCheck className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* SCREEN 7: Welcome & Congrats Screen */}
        {step === 7 && (
          <div className="text-center space-y-6 py-4">
            <div className="w-20 h-20 bg-gradient-to-tr from-green-500 to-emerald-400 rounded-3xl mx-auto flex items-center justify-center text-white shadow-xl shadow-green-500/30 transform animate-bounce">
              <FiCheck className="w-10 h-10" />
            </div>

            <div className="space-y-2">
              <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                Welcome, {formData.studentFirstName}! 🎉
              </h2>
              <p className="text-gray-600 text-sm max-w-sm mx-auto leading-relaxed">
                Your 7-day free trial account has been successfully set up for{' '}
                <strong className="text-gray-900">{formData.standard} ({formData.board})</strong>.
              </p>
            </div>

            <div className="p-4 bg-blue-50/70 border border-blue-100 rounded-2xl text-xs text-blue-900 space-y-1">
              <p><strong>Registered Email:</strong> {formData.parentEmail}</p>
              <p><strong>WhatsApp Support:</strong> {formData.parentWhatsappNumber}</p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl shadow-xl shadow-blue-500/25 transition flex items-center justify-center gap-2 text-base"
            >
              Proceed to Login <FiArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default RegisterPageNew;
